import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Clock, MapPin, Users } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { pick } from "../i18n/strings";

export default function ExperienceDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [exp, setExp] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/experiences/${id}`).then((res) => setExp(res.data));
  }, [id]);

  if (!exp) {
    return (
      <div className="px-6 pt-10 text-brand-tan tracking-[0.4em] uppercase text-xs">
        ARISTEIA
      </div>
    );
  }

  const dateStr = new Date(exp.date).toLocaleDateString(
    lang === "ca" ? "ca-ES" : lang === "en" ? "en-GB" : "es-ES",
    { day: "2-digit", month: "long", year: "numeric" },
  );

  return (
    <div data-testid="experience-detail">
      {/* Photo-based hero (replaces the welcome-page brand video) */}
      <div className="relative w-full h-[65vh] md:h-[78vh] overflow-hidden bg-brand-deep-brown">
        <img
          src={exp.image}
          alt={pick(exp.title, lang)}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="exp-hero-photo"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/30" />
        <div className="absolute top-0 inset-x-0 flex justify-between px-6 md:px-12 pt-6 z-20">
          <button
            onClick={() => navigate(-1)}
            data-testid="exp-back"
            className="inline-flex items-center gap-2 text-brand-cream/90 text-xs tracking-[0.25em] uppercase font-bold hover:text-brand-cream"
          >
            <ArrowLeft size={14} strokeWidth={1.75} />
            {t("home")}
          </button>
          <LanguageSwitcher tone="cream" />
        </div>
        <div className="absolute bottom-0 inset-x-0 px-6 md:px-12 pb-8 md:pb-14 z-10">
          <div className="mx-auto max-w-5xl">
            <p className="text-[10px] tracking-[0.45em] uppercase font-bold text-brand-gold">
              {t("season_label")} I · {t("chapter")} {exp.chapter}
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl leading-[1.04] text-brand-cream mt-3 max-w-[18ch]">
              {pick(exp.title, lang)}
            </h1>
            <p className="text-brand-cream/85 text-base md:text-lg mt-4 max-w-[44ch] font-light italic">
              {pick(exp.subtitle, lang)}
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 md:px-12 pt-8 md:pt-16">
        <div className="grid grid-cols-3 gap-3 md:gap-6 max-w-xl">
          <Meta icon={MapPin} label={t("location")} value={exp.location} />
          <Meta
            icon={Clock}
            label={t("duration")}
            value={`${exp.duration_hours}${t("hours")}`}
          />
          <Meta icon={Users} label={t("capacity")} value={exp.capacity} />
        </div>

        <div className="md:grid md:grid-cols-3 md:gap-16 mt-8 md:mt-14">
          <div className="md:col-span-2">
            <p className="text-brand-brown leading-relaxed text-base md:text-lg">
              {pick(exp.description, lang)}
            </p>

            <section className="mt-10">
              <p className="text-[10px] tracking-[0.4em] uppercase font-bold text-brand-tan">
                {t("itinerary")}
              </p>
              <ul className="mt-5 border-l border-brand-tan/30 pl-5 space-y-5">
                {exp.itinerary.map((it, i) => (
                  <li key={`${it.time}-${i}`} className="relative">
                    <span className="absolute -left-[26px] top-1 w-2 h-2 rounded-full bg-brand-gold" />
                    <p className="text-xs tracking-[0.25em] uppercase font-bold text-brand-tan">
                      {it.time}
                    </p>
                    <p className="text-brand-brown font-medium mt-1">
                      {pick(it.title, lang)}
                    </p>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mt-10 rounded-2xl bg-brand-ivory/60 border border-brand-tan/25 p-6 md:p-8">
              <p className="text-[10px] tracking-[0.4em] uppercase font-bold text-brand-green">
                {t("gastronomy")}
              </p>
              <p className="mt-2 text-brand-brown leading-relaxed font-light italic">
                {pick(exp.gastronomy, lang)}
              </p>
            </section>
          </div>

          <aside className="mt-10 md:mt-0">
            <div className="md:sticky md:top-28 rounded-2xl border border-brand-tan/30 bg-white/70 p-6 md:p-8">
              <p className="text-[10px] tracking-[0.4em] uppercase text-brand-tan font-bold">
                {dateStr}
              </p>
              <p className="font-serif text-4xl text-brand-brown leading-none mt-3">
                €{exp.price_eur}
                <span className="text-sm font-sans font-medium text-brand-tan ml-1">
                  {t("per_person")}
                </span>
              </p>
              {exp.sold_out ? (
                <>
                  <div
                    className="mt-6 w-full text-center py-4 rounded-2xl bg-brand-tan/20 text-brand-deep-brown font-bold tracking-[0.22em] uppercase text-sm cursor-not-allowed select-none"
                    data-testid="exp-sold-out-cta"
                  >
                    {t("sold_out")}
                  </div>
                  <p className="mt-3 text-xs text-brand-brown/70 leading-relaxed">
                    {t("sold_out_note")}
                  </p>
                </>
              ) : (
                <Link
                  to={`/book/${exp.id}`}
                  data-testid="exp-book-cta"
                  className="mt-6 inline-flex w-full items-center justify-center py-4 rounded-2xl bg-brand-brown text-brand-cream font-bold tracking-[0.18em] uppercase text-sm hover:bg-brand-deep-brown transition-colors active:scale-[0.98]"
                >
                  {t("book_now")}
                </Link>
              )}
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-brand-tan/25 p-3 md:p-4">
      <div className="flex items-center gap-1.5 text-brand-tan">
        <Icon size={13} strokeWidth={1.5} />
        <p className="text-[9px] tracking-[0.25em] uppercase font-bold">
          {label}
        </p>
      </div>
      <p className="text-sm md:text-base font-bold text-brand-brown mt-1.5 truncate">
        {value}
      </p>
    </div>
  );
}
